/**
 * src/hooks/useWhisperRecording.js — Chunked Whisper recording hook (Phase 89-01)
 *
 * Records audio with rolling 60s MediaRecorder chunks and streams them to the
 * server via `voice.upload_chunk`. On stop, calls `voice.finalize_recording`
 * with `wait_for_transcript: true` to receive the stitched transcript.
 *
 * No hard recording cap — long dictations are broken into independently-decodable
 * webm chunks using a parallel-swap pattern (new recorder starts before old stops).
 *
 * Usage:
 *   const { recording, transcribing, startRecording, stopRecording,
 *           durationMs, chunksUploaded, chunksPending } = useWhisperRecording({
 *     onTranscript: (text) => setInput(text),
 *     contextType: 'project',
 *     contextId: activeProjectId,
 *     contextHint: 'agent_chat',
 *   });
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { getAuthHeaders } from '../lib/api/client';
import { startTimer, logVoiceEvent } from '../lib/voice/voiceLogger';
import { resolveDefaultProvider } from '../lib/voice/sttProvider';
import { emitVoiceEvent } from '../lib/voiceEvents';
import { logVoiceCaptureFailure } from '../lib/voice/logVoiceCaptureFailure';
import { registerMic, releaseMic, stopAllMics } from '../lib/voice/micRegistry';
import { pickRecorderMime } from '../lib/voice/audioGuards';
// TICKET-VOICE-CONVO-UVTP (CARL development-005, 2026-05-07) — conversation
// mode now routes through the unified UVTP orchestrator instead of the legacy
// /api/ai voice.upload_chunk + voice.finalize_recording chunk-stream path.
// transcribeViaUvtp returns the stitched transcript ASAP (≤4s typical fast
// path), eliminating the 8-25s Whisper finalize lag that produced CEO's
// "stuck on Thinking 20s+ with no feedback" symptom.
import { transcribeViaUvtp } from '../lib/api/voice-v2';
// T3 streaming-STT (spec docs/plans/2026-06-06-streaming-stt-endpoint-spec.md).
// Conversation mode opens a live PCM16 WebSocket to the stt-proxy when the flag
// + proxy URL are set; the orchestration lives in conversationStreaming.js to
// keep this hook under the 500-line split ceiling. Dictation is untouched.
import { streamingSttFlagOn, getStreamingSttProxyUrl, getStreamingSttModel } from '../lib/voice/voiceProcessFn';
import { startConversationStreamer } from '../lib/voice/conversationStreaming';
import {
  classifyFinalizeError,
  FINALIZE_TIMEOUT_MS,
  CHUNK_UPLOAD_TIMEOUT_MS,
  PARTIAL_TRANSCRIPT_TIMEOUT_MS,
} from './useWhisperRecording.helpers';

// T3 — how long the finalize seam waits for the streamed final after commit()
// before salvaging the turn with the accumulated interim (or, with no interim,
// falling back to the buffered-blob UVTP path). TICKET-VOICE-FINAL-DEADLOCK:
// was 1500ms, but the commit→OpenAI→proxy→client final round trip is ~0.5-2s
// on mobile networks; 4s still beats the UVTP fallback's own latency and the
// interim salvage usually resolves the turn well before this elapses.
const STREAMING_FINAL_DEADLINE_MS = 4000;

const CHUNK_DURATION_DICTATION_MS = 60_000; // 60s per chunk — Whisper sweet-spot
const CHUNK_DURATION_CONVERSATION_MS = 1500; // 1.5s per chunk — walk-and-talk partials (Phase 90-02)
const VOICE_API = '/api/ai';
const UPLOAD_RETRIES = 3;

/**
 * Negotiate best supported MIME type for MediaRecorder. TICKET-1090: reuse the
 * shared preference list (includes audio/mp4 for iOS Safari) so the blob / UVTP
 * label names the container actually recorded. '' = let the browser choose.
 */
function negotiateMimeType() {
  const mime = pickRecorderMime();
  try {
    return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(mime) ? mime : '';
  } catch { return ''; }
}

/** Convert a Blob to base64 (strips data: prefix). */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Generate a recording id — prefer crypto.randomUUID, fall back to timestamp+random. */
function makeRecordingId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function useWhisperRecording({
  onTranscript, onError, contextType, contextId, contextHint,
  mode = 'dictation', // 'dictation' | 'conversation' (Phase 90-02)
  onPartial,          // (text, chunkIndex) — fires per chunk in conversation mode
  // 2026-04-21 (C1/W4 root-cause fix) — language hint forwarded to Whisper on
  // voice.upload_chunk + voice.partial_transcript. Previously hardcoded 'auto',
  // which the server maps to undefined → Whisper auto-detects → on silence it
  // emits Russian/Ukrainian YouTube boilerplate. Callers (useVoiceConversation)
  // must thread the user's selected language (en | es | mix | auto) from
  // voiceOptions.getStoredLanguage(). Defaults to 'en' for safety if unset.
  language = 'en',
} = {}) {
  const chunkDurationMs = mode === 'conversation'
    ? CHUNK_DURATION_CONVERSATION_MS
    : CHUNK_DURATION_DICTATION_MS;
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [chunksUploaded, setChunksUploaded] = useState(0);
  const [chunksPending, setChunksPending] = useState(0);

  const recordingIdRef = useRef(null);
  // PAUL Plan 92-01 — trace_id minted at recording_start, threaded through
  // every logVoiceEvent + the X-Trace-Id header on every chunk POST and
  // finalize POST so the entire client→server pipeline shows one trace
  // in /admin/observability.
  const traceIdRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const mimeTypeRef = useRef('');
  // 2026-04-20 — sticky mute state. Previous muteMic only toggled enabled
  // on the current stream's tracks, which didn't help in auto-VAD mode
  // where streams are acquired lazily. Now muteMic sets this ref, and
  // every new stream applies the ref on acquisition.
  const micMutedRef = useRef(false);
  const chunkTimerRef = useRef(null);
  const durationTimerRef = useRef(null);
  const startTimeRef = useRef(0);
  const uploadPromisesRef = useRef([]);
  const voiceRecordIdRef = useRef(null);
  // TICKET-VOICE-CONVO-UVTP (2026-05-07) — local accumulator for conversation
  // mode chunks. Each rotated MediaRecorder.onstop pushes its Blob here;
  // stopRecording concatenates them into one Blob and POSTs once to UVTP via
  // transcribeViaUvtp. The legacy chunk-upload path (uploadChunk →
  // voice.upload_chunk per 1.5s) is now dictation-only.
  const localChunkBlobsRef = useRef([]);
  // TICKET-VOICE-FINAL-DEADLOCK — conversation mode no longer rotates
  // recorders (see rotateRecorder), so the runaway guard counts timer ticks
  // instead of chunk indices there. Reset per recording.
  const rotationTicksRef = useRef(0);
  // 2026-04-20 Phase 1 (audit P0-3) — ghost-send cancel token. stopRecording
  // dispatches a detached finalize IIFE that calls onTranscript when the
  // server returns. If the user hangs up (endSession) mid-finalize, that
  // callback still fires and the consuming agent pipes the transcript into
  // a fresh sendToAgent → "ghost turn" after hangup. finalizeGenRef is
  // captured inside the IIFE; cancelPendingTranscript() increments it and
  // aborts the in-flight fetch so the captured gen no longer matches and
  // onTranscript is suppressed.
  const finalizeGenRef = useRef(0);
  const finalizeAbortRef = useRef(null);
  // T3 streaming-STT — the live PCM16 streamer controller (conversationStreaming.js)
  // and a fast boolean the onstop partial-suppression + finalize seam read.
  // streamingActiveRef flips true ONLY when streamer.start() succeeded inside
  // the user gesture; on any failure it stays false and the MediaRecorder +
  // UVTP fallback path runs exactly as today.
  const streamingSttRef = useRef(null);
  const streamingActiveRef = useRef(false);

  const cleanup = useCallback(() => {
    // 2026-04-20 — defensive nuclear cleanup. Observability caught a runaway
    // where chunk indices reached 14086+ across an 8-hour session, producing
    // hundreds of orphaned `chunk_upload` rows per minute while the user was
    // on an entirely different screen. Root cause: previous cleanup only
    // nulled mediaRecorderRef without calling .stop() on the live recorder.
    // When a rotate was mid-flight or the stream hadn't torn down, the
    // browser kept the MediaRecorder alive, and `setInterval(rotateRecorder)`
    // kept chaining new recorders on the existing stream. Now we
    // unconditionally .stop() the current recorder AND null it.
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch { /* noop */ }
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      releaseMic(streamRef.current);
      streamRef.current = null;
    }
    // T3 — release the streaming-STT streamer's own mic + socket if one is still
    // live (e.g. unmount mid-record before the finalize seam detached it).
    if (streamingSttRef.current) {
      try { streamingSttRef.current.stop(); } catch { /* spine-emit-ok: stop is idempotent mic/socket release */ }
      streamingSttRef.current = null;
      streamingActiveRef.current = false;
    }
  }, []);

  // Release mic + timers on unmount — prevents red-dot persistence when the
  // consuming component (VoiceSessionOverlay, AgentChat) unmounts mid-record.
  useEffect(() => {
    return () => {
      try {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state === 'recording') rec.stop();
      } catch { /* noop */ }
      cleanup();
    };
  }, [cleanup]);

  /** Upload a single chunk with retry, tracking pending/uploaded counts.
   *  TICKET-VOICE-CONVO-UVTP — dictation-only path. Conversation mode
   *  accumulates blobs locally and submits once via transcribeViaUvtp.
   *  The `if (mode !== 'conversation')` belt-and-suspenders guard below
   *  is defensive: createChunkRecorder.onstop already skips this call
   *  for conversation mode, but a future refactor that accidentally
   *  reaches uploadChunk from a conversation path lands as a no-op
   *  instead of silently posting voice.upload_chunk to /api/ai. */
  const uploadChunk = useCallback(async (blob, chunkIndex, isFinal) => {
    if (mode !== 'conversation') {
      // dictation-only: voice.upload_chunk path against /api/ai
    } else {
      return; // conversation mode never streams chunks; UVTP gets the full blob
    }
    setChunksPending(p => p + 1);
    const timer = startTimer('chunk_upload');

    let lastError = null;
    let permanent = false;
    try {
      for (let attempt = 0; attempt < UPLOAD_RETRIES; attempt++) {
        try {
          const base64 = await blobToBase64(blob);
          const auth = await getAuthHeaders();
          const resp = await fetch(VOICE_API, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(traceIdRef.current ? { 'X-Trace-Id': traceIdRef.current } : {}),
              ...auth,
            },
            // TICKET-047: hard timeout so a hung upload can't pin the recorder
            // in "Transcribing audio…" forever. Hook's outer UPLOAD_RETRIES
            // loop catches the AbortError and retries with linear backoff.
            signal: AbortSignal.timeout(CHUNK_UPLOAD_TIMEOUT_MS),
            body: JSON.stringify({
              action: 'voice.upload_chunk',
              recording_id: recordingIdRef.current,
              trace_id: traceIdRef.current,
              chunk_index: chunkIndex,
              audio: base64,
              mime_type: mimeTypeRef.current || 'audio/webm',
              is_final_chunk: !!isFinal,
              context_type: contextType || undefined,
              context_id: contextId || undefined,
              language,
              stt_provider: resolveDefaultProvider(),
            }),
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok || data.error) {
            const err = new Error(data.error || `Upload failed (${resp.status})`);
            // 4xx = permanent client error. Retrying floods the console and can't
            // recover (e.g., 404 "Voice record not found" is the same every attempt).
            if (resp.status >= 400 && resp.status < 500) {
              permanent = true;
              throw err;
            }
            throw err;
          }
          timer.ok(`chunk ${chunkIndex} uploaded`, { bytes: blob.size, attempt });
          setChunksUploaded(n => n + 1);
          if (!voiceRecordIdRef.current && data.voice_record_id) {
            voiceRecordIdRef.current = data.voice_record_id;
          }
          return;
        } catch (err) {
          lastError = err;
          if (permanent) break;
          if (attempt < UPLOAD_RETRIES - 1) {
            await new Promise(r => setTimeout(r, 500 * (attempt + 1))); // linear backoff
          }
        }
      }
    } finally {
      setChunksPending(p => p - 1);
    }
    timer.fail(`chunk ${chunkIndex} upload failed${permanent ? ' (permanent)' : ` after ${UPLOAD_RETRIES} attempts`}: ${lastError?.message}`);
    throw lastError || new Error('Chunk upload failed');
  }, [contextType, contextId, language]);

  /** Post a chunk to voice.partial_transcript for conversation-mode preview text. */
  const postPartialTranscript = useCallback(async (blob, chunkIndex, mime) => {
    const base64 = await blobToBase64(blob);
    const auth = await getAuthHeaders();
    const resp = await fetch(VOICE_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(traceIdRef.current ? { 'X-Trace-Id': traceIdRef.current } : {}),
        ...auth,
      },
      // TICKET-047: hard timeout — partials are 1.5s preview chunks, not the
      // source of truth. If the request hangs, drop it silently rather than
      // letting AbortError bubble up; the dictation chunk path delivers the
      // real transcript.
      signal: AbortSignal.timeout(PARTIAL_TRANSCRIPT_TIMEOUT_MS),
      body: JSON.stringify({
        action: 'voice.partial_transcript',
        audio_base64: base64,
        mime_type: mime || 'audio/webm',
        recording_id: recordingIdRef.current,
        trace_id: traceIdRef.current,
        chunk_index: chunkIndex,
        language,
      }),
    });
    const data = await resp.json().catch(() => ({}));
    if (data && data.ok && data.text && onPartial) {
      onPartial(data.text, chunkIndex);
    }
  }, [onPartial, language]);

  /** Create a new MediaRecorder that uploads its chunk on stop. */
  const createChunkRecorder = useCallback((stream, mime, isFinal = false) => {
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const localChunks = [];
    const myIndex = chunkIndexRef.current++;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) localChunks.push(e.data);
    };

    recorder.onstop = () => {
      if (localChunks.length === 0) return;
      const blob = new Blob(localChunks, { type: mime || 'audio/webm' });
      if (blob.size === 0) return;
      if (mode === 'conversation') {
        // TICKET-VOICE-CONVO-UVTP — accumulate locally; stopRecording submits
        // the concatenated blob once to /api/voice-transcribe via
        // transcribeViaUvtp. No per-chunk POST, no voice.upload_chunk action.
        localChunkBlobsRef.current.push(blob);
      } else {
        // Dictation mode — legacy chunk-upload path against /api/ai
        // voice.upload_chunk. Stays unchanged for useDictation consumers.
        const p = uploadChunk(blob, myIndex, isFinal).catch((err) => {
          console.error(`[useWhisperRecording] chunk ${myIndex} upload error:`, err.message);
          if (onError) onError(err.message);
        });
        uploadPromisesRef.current.push(p);
      }

      // Phase 90-02: conversation mode fires a partial transcript in parallel
      // with the upload. Fire-and-forget; final stitched transcript still
      // comes from voice.finalize_recording on stop.
      // 2026-04-20 (session 4, P0-A3 fix) — was `.catch(() => { /* ignore */ })`.
      // Partial transcripts drive the real-time UI echo in conversation mode.
      // When they silently fail, the user keeps talking with no visible feedback,
      // chunks grow, and the whole batch dies at finalize. Log so SQL can surface
      // the frequency; do NOT surface to UX (best-effort by design).
      // T3: when the streaming-STT socket is active it provides partials
      // directly (onPartial in conversationStreaming.js), so skip the
      // voice.partial_transcript POST to avoid duplicate/competing live text.
      // The localChunkBlobsRef.current.push(blob) above stays UNCONDITIONAL so
      // the streamer's fallback (UVTP on the buffered blob) always has audio.
      if (mode === 'conversation' && onPartial && !streamingActiveRef.current) {
        postPartialTranscript(blob, myIndex, mime).catch((err) => {
          logVoiceEvent('partial_transcript', 'error', err?.message || 'partial_failed', {
            chunk_index: myIndex,
            blob_size: blob.size,
          });
        });
      }
    };

    return recorder;
  }, [uploadChunk, onError, mode, onPartial, postPartialTranscript]);

  /** Rotate: start a new recorder BEFORE stopping old — minimal audio gap. */
  const rotateRecorder = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const oldRecorder = mediaRecorderRef.current;
    if (!oldRecorder || oldRecorder.state !== 'recording') return;

    // 2026-04-20 — hard runaway cap. If chunk index crosses this threshold
    // (~500 × 1.5s = 12.5 min of conversation mode, or ~500 × 60s = 8h of
    // dictation mode — both way beyond any legitimate recording), assume
    // we're leaking. Force-stop the recorder, kill the timer, release the
    // stream. Observability (voice_error step=runaway_guard) surfaces the
    // condition without spamming chunk_upload rows. Conversation mode counts
    // timer TICKS (rotationTicksRef) since it no longer rotates (below).
    const MAX_CHUNK_INDEX = 500;
    const runawayCount = mode === 'conversation'
      ? (rotationTicksRef.current += 1)
      : chunkIndexRef.current;
    if (runawayCount >= MAX_CHUNK_INDEX) {
      console.warn('[useWhisperRecording] runaway guard tripped at', runawayCount, '— force-stopping');
      logVoiceEvent('runaway_guard', 'error', `Chunk counter crossed ${MAX_CHUNK_INDEX}; forced stop`, {
        chunk_index: runawayCount,
        mode,
      });
      try { oldRecorder.stop(); } catch { /* noop */ }
      if (chunkTimerRef.current) { clearInterval(chunkTimerRef.current); chunkTimerRef.current = null; }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => { try { t.stop(); } catch { /* noop */ } });
        streamRef.current = null;
      }
      mediaRecorderRef.current = null;
      return;
    }

    // TICKET-VOICE-FINAL-DEADLOCK — conversation mode does NOT rotate. Each
    // parallel-swap chunk is a COMPLETE standalone webm; the UVTP fallback
    // concatenated 5-11 of them into one blob and Whisper rejected it
    // (HTTP 400 "Audio file might be corrupted"), killing the turn. One
    // continuous recorder produces ONE valid webm on stop — a fallback blob
    // that actually transcribes. Live partials come from the streaming-STT
    // socket; the per-chunk postPartialTranscript echo (streaming-off only)
    // is deliberately sacrificed — a working turn beats a live echo.
    // Dictation mode keeps its 60s rotation (Whisper sweet spot) unchanged.
    if (mode === 'conversation') return;

    const newRecorder = createChunkRecorder(stream, mimeTypeRef.current, false);
    newRecorder.start();
    mediaRecorderRef.current = newRecorder;
    // Stop old last — its onstop fires with full-decodable webm
    oldRecorder.stop();
  }, [createChunkRecorder, mode]);

  const startRecording = useCallback(async () => {
    if (recording || transcribing) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      registerMic(stream, null, `useWhisperRecording:${mode}`);
      // Honor sticky mute — new stream inherits the user's prior mute
      // choice even though the tracks were just created.
      if (micMutedRef.current) {
        try { stream.getAudioTracks().forEach((t) => { t.enabled = false; }); } catch { /* noop */ }
      }

      const mime = negotiateMimeType();
      mimeTypeRef.current = mime;
      recordingIdRef.current = makeRecordingId();
      // PAUL Plan 92-01 — mint a fresh trace per recording. crypto.randomUUID
      // is universally available in modern browsers; fall back to recording_id
      // (already uuid-shaped) if absent.
      traceIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : recordingIdRef.current;
      chunkIndexRef.current = 0;
      rotationTicksRef.current = 0;
      uploadPromisesRef.current = [];
      voiceRecordIdRef.current = null;

      setChunksUploaded(0);
      setChunksPending(0);
      setDurationMs(0);
      startTimeRef.current = Date.now();

      // Defensive — logVoiceEvent may be a stub in tests that mock voiceLogger
      // with only `{ startTimer }`. The behaviour is identical in prod
      // (logVoiceEvent is always exported) so the guard is mock-only safety.
      try {
        logVoiceEvent?.('recording_start', 'start', `mode=${mode} mime=${mime}`, {
          trace_id: traceIdRef.current,
          recording_id: recordingIdRef.current,
          mode,
          mime,
          page: typeof window !== 'undefined' ? window.location.pathname : null,
        });
      } catch { /* mock-safe */ }

      const recorder = createChunkRecorder(stream, mime, false);
      mediaRecorderRef.current = recorder;
      recorder.onerror = () => {
        cleanup();
        setRecording(false);
        if (onError) onError('Recording failed');
      };
      recorder.start();
      setRecording(true);

      // Rolling chunk boundary
      chunkTimerRef.current = setInterval(rotateRecorder, chunkDurationMs);
      // Duration tick (UI feedback)
      durationTimerRef.current = setInterval(() => {
        setDurationMs(Date.now() - startTimeRef.current);
      }, 250);

      // T3 streaming-STT — engage ONLY for conversation mode behind the flag +
      // a configured proxy URL. The MediaRecorder above keeps running regardless
      // (it buffers localChunkBlobsRef for the UVTP fallback). streamer.start()
      // MUST run inside this user-gesture call (it resumes the AudioContext).
      // Failure is non-fatal: startConversationStreamer returns null on any
      // error (after emitting stt_stream.connect/error), leaving the fallback
      // path exactly as today.
      streamingSttRef.current = null;
      streamingActiveRef.current = false;
      if (mode === 'conversation' && streamingSttFlagOn() && getStreamingSttProxyUrl()) {
        try {
          const controller = await startConversationStreamer({
            proxyUrl: getStreamingSttProxyUrl(),
            traceId: traceIdRef.current,
            language,
            model: getStreamingSttModel(),
            onPartial,
          });
          if (controller) {
            streamingSttRef.current = controller;
            streamingActiveRef.current = true;
          }
        } catch (err) {
          // startConversationStreamer is designed not to throw, but guard the
          // seam regardless so a streamer bug never breaks plain recording.
          logVoiceEvent('stt_stream.connect', 'error', err?.message || 'streaming engage failed', {
            trace_id: traceIdRef.current,
            error_code: 'start_failed',
          });
          streamingSttRef.current = null;
          streamingActiveRef.current = false;
        }
      }
    } catch (err) {
      cleanup();
      setRecording(false);
      // BUG-019 — getUserMedia failures (iOS Safari "PTT not recording") were
      // invisible: this catch only called onError, so ZERO app_error_logs rows
      // landed for the capture path. Emit to BOTH the dedicated voice-capture
      // failure logger (correlates start->end, surfaces failure_stage) and the
      // observability spine (logVoiceEvent error row) so the failure is
      // diagnosable from a desktop session. SPINE EMIT CONTRACT: this hot-zone
      // catch must emit. Capture behavior (onError) is unchanged.
      try {
        logVoiceCaptureFailure({
          from_hook: 'useWhisperRecording',
          context_type: contextType,
          context_hint: contextHint,
          failure_stage: 'getUserMedia_denied',
          error: err,
        });
      } catch { /* spine-emit-ok: logVoiceCaptureFailure is fire-and-forget; logVoiceEvent below still emits */ }
      try {
        logVoiceEvent?.('recording_start', 'error', err?.message || 'Microphone access denied', {
          trace_id: traceIdRef.current,
          recording_id: recordingIdRef.current,
          mode,
          error_code: err?.name || 'getUserMedia_error',
          error_message: err?.message || 'Microphone access denied',
          page: typeof window !== 'undefined' ? window.location.pathname : null,
        });
      } catch { /* spine-emit-ok: mock-safe — voiceLogger may be a partial stub in tests */ }
      if (onError) onError(err.message || 'Microphone access denied');
    }
  }, [recording, transcribing, onError, cleanup, createChunkRecorder, rotateRecorder, contextType, contextHint, mode, language, onPartial]);

  const stopRecording = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;

    // Stop rolling timers first so no rotate fires during teardown
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }

    recorder.stop();

    // Release mic tracks NOW — the browser's "tab is using your microphone"
    // indicator must clear the moment the user stops. We don't need the stream
    // for finalize (chunks are already buffered in uploadPromisesRef).
    if (streamRef.current) {
      releaseMic(streamRef.current);
      streamRef.current = null;
    }

    setRecording(false);
    setTranscribing(true);

    // Snapshot refs — detached IIFE below runs after cleanup() clears them
    const recordingId = recordingIdRef.current;
    const pending = uploadPromisesRef.current;
    // 2026-06-06 (T3 ghost-send hardening) — capture the finalize generation
    // SYNCHRONOUSLY here, before the awaits below. A session close
    // (cancelPendingTranscript/forceCleanup) any time after stop — including
    // during the 5s upload-settle wait or while the streamed final is in
    // flight — bumps finalizeGenRef so the captured myGen no longer matches and
    // the transcript (streamed OR UVTP) is dropped instead of ghost-sent into a
    // closed session. Previously captured AFTER the awaits, which left a window
    // where a close racing the gen capture failed to suppress the send.
    const myGen = finalizeGenRef.current;

    // Emit record_stop SYNCHRONOUSLY so listeners (useVoiceBreadcrumbs)
    // can insert an optimistic "Processing..." row before any network wait.
    emitVoiceEvent('voice:record_stop', {
      recordingId,
      contextType: contextType || null,
      contextId: contextId || null,
    });

    // TICKET-CODEX-3368864100 — snapshot + stop the streaming-STT controller
    // NOW, before the upload-settle wait below, so the browser mic indicator
    // extinguishes promptly when the user presses stop.  The streamer's own
    // getUserMedia stream and WebSocket remain open until stop() is called;
    // delaying until after awaitFinal (inside the conversation branch, up to 5s
    // later) keeps the mic indicator on longer than expected.  stop() commits
    // + closes the socket so the proxy produces the final; awaitFinal() in the
    // conversation branch still resolves because the finalPromise is already
    // pending in conversationStreaming.js and resolves when onFinal fires (before
    // or after the socket closes).
    const preSettleStreamer = streamingSttRef.current;
    const preSettleStreamingActive = streamingActiveRef.current;
    if (preSettleStreamer && mode === 'conversation') {
      streamingSttRef.current = null;
      streamingActiveRef.current = false;
      try { preSettleStreamer.stop(); } catch { /* spine-emit-ok: stop is idempotent mic/socket release */ }
    }

    // Wait up to 5s for in-flight chunk uploads to settle, then emit
    // voice:upload_complete so breadcrumb listeners start polling.
    // Finalize fires in a detached IIFE so stopRecording returns and the
    // mic button is freed immediately.
    await new Promise(r => setTimeout(r, 0)); // let onstop microtask fire
    await Promise.race([
      Promise.allSettled(pending),
      new Promise(r => setTimeout(r, 5000)),
    ]);

    const capturedVoiceRecordId = voiceRecordIdRef.current;
    emitVoiceEvent('voice:upload_complete', {
      voiceRecordId: capturedVoiceRecordId,
      recordingId,
      contextType: contextType || null,
      contextId: contextId || null,
    });

    // Detach finalize — the user's mic is already released and breadcrumb
    // listeners are polling. We still want the stitched transcript for
    // onTranscript consumers (AgentChat input), but the UI is no longer blocked.
    //
    // 2026-04-20 Phase 1 (audit P0-3 ghost-send): myGen is captured
    // SYNCHRONOUSLY above (before the awaits). If the session is closed
    // (cancelPendingTranscript()) before the fetch/stream resolves, the ref is
    // incremented and the captured myGen no longer matches — we log-and-drop
    // instead of firing onTranscript into a closed session.

    // TICKET-VOICE-CONVO-UVTP (CARL development-005, 2026-05-07) — conversation
    // mode submits the concatenated blob to /api/voice-transcribe (UVTP) and
    // delivers the transcript via onTranscript. Bypasses the 8-25s
    // voice.finalize_recording lag entirely. Dictation mode falls through to
    // the legacy IIFE below.
    if (mode === 'conversation') {
      // T3 streaming-STT — use the pre-settle snapshot captured above.
      // streamingSttRef was already cleared and stop() already called before the
      // settle wait (TICKET-CODEX-3368864100); reuse those captured values here.
      const streamer = preSettleStreamer;
      const streamingActive = preSettleStreamingActive;

      // The buffered-blob UVTP path — unchanged batch behavior. Used both when
      // streaming is OFF and as the automatic fallback when the streamed final
      // doesn't arrive (timeout) or the stream errored.
      const runUvtpFallback = async () => {
        const timer = startTimer('uvtp_finalize');
        // TICKET-CODEX-3199616574: wire an AbortController into finalizeAbortRef
        // so cancelPendingTranscript() / forceCleanup() can abort the in-flight
        // UVTP request (mirrors how the dictation path uses finalizeAbortRef).
        const uvtpAbort = new AbortController();
        finalizeAbortRef.current = uvtpAbort;
        const blobs = localChunkBlobsRef.current;
        // Hand off the buffer; subsequent recordings get a fresh array.
        localChunkBlobsRef.current = [];
        try {
          const fullBlob = new Blob(blobs, { type: mimeTypeRef.current || 'audio/webm' });
          if (fullBlob.size === 0) {
            timer.fail('empty_audio');
            logVoiceEvent('uvtp_finalize', 'error', 'concatenated audio is 0 bytes', {
              chunk_count: blobs.length,
              recording_id: recordingId,
              trace_id: traceIdRef.current,
            });
            if (onError) onError('No audio captured', { stage: 'uvtp_empty' });
            return;
          }
          const audioBase64 = await blobToBase64(fullBlob);
          const durationSec = Math.round((Date.now() - startTimeRef.current) / 1000);
          // 2026-06-06 conversation-capture diagnostics — emit the INPUT
          // audio identity (byte size + duration) BEFORE STT. The spine
          // previously recorded only OUTPUT tts audio_bytes; on a silent-mic
          // capture we had no way to see whether the recorded blob was tiny
          // (mic never opened) vs full-but-silent. This makes the mic-capture
          // root observable on the next device test (pairs with the new
          // tts_playback events on the output side).
          logVoiceEvent('stt_input', 'ok', `conversation audio captured: ${(fullBlob.size / 1024).toFixed(0)}KB`, {
            audio_bytes: fullBlob.size,
            duration_ms: durationSec * 1000,
            chunk_count: blobs.length,
            mime_type: mimeTypeRef.current || 'audio/webm',
            recording_id: recordingId,
            trace_id: traceIdRef.current,
          });
          // This UVTP call is reachable ONLY from the `if (mode === 'conversation')`
          // branch (the dictation path uses voice.finalize_recording below). It
          // serves both the streaming-OFF batch path and the streaming fallback.
          const result = await transcribeViaUvtp({
            audioBase64,
            mimeType: mimeTypeRef.current || 'audio/webm',
            durationSec,
            source: 'assistant',
            signal: uvtpAbort.signal,
          });
          // Generation bumped → session was cancelled. Drop the response.
          if (myGen !== finalizeGenRef.current) {
            timer.ok(`uvtp_finalize cancelled by session close (gen ${myGen} → ${finalizeGenRef.current})`);
            logVoiceEvent('uvtp_finalize_cancelled', 'ok', 'Session closed before UVTP returned', {
              captured_gen: myGen,
              current_gen: finalizeGenRef.current,
              recording_id: recordingId,
            });
            return;
          }
          timer.ok(`uvtp_finalize ok: ${(result.text || '').length} chars`, {
            uvtp_run_id: result.uvtpRunId,
            path: result.path,
            provider: result.provider,
            fallback_used: result.fallbackUsed,
            heavy_path_polled: result.heavyPathPolled,
            // 2026-06-06 — server-measured STT duration. Kept under
            // server_stt_ms because timer.ok() forces duration_ms to the
            // client finalize wall-clock; without this the true STT time
            // was lost (the baseline showed stt rows without it).
            server_stt_ms: result.durationMs,
            recording_id: recordingId,
            trace_id: traceIdRef.current,
          });
          if (onTranscript) onTranscript(result.text || '');
          emitVoiceEvent('voice:finalize_complete', {
            voiceRecordId: capturedVoiceRecordId,
            recordingId,
            contextType: contextType || null,
            contextId: contextId || null,
          });
        } catch (err) {
          if (myGen !== finalizeGenRef.current) {
            logVoiceEvent('uvtp_finalize_cancelled', 'ok', 'UVTP aborted by session close', {
              captured_gen: myGen,
              current_gen: finalizeGenRef.current,
              recording_id: recordingId,
            });
            return;
          }
          const msg = err?.message || 'UVTP transcription failed';
          // TICKET-CODEX-3367812608: timer.fail() already calls
          // logVoiceEvent('uvtp_finalize', 'error', ...) via startTimer;
          // the explicit logVoiceEvent below was a duplicate that caused
          // double-counting in app_error_logs. Removed.
          timer.fail(msg);
          console.error('[useWhisperRecording] uvtp finalize error:', msg);
          if (onError) onError(msg, { stage: 'uvtp' });
          emitVoiceEvent('voice:finalize_error', {
            voiceRecordId: capturedVoiceRecordId,
            recordingId,
            error: msg,
            stage: 'uvtp',
          });
        } finally {
          // Clear ref so cancelPendingTranscript/forceCleanup don't double-abort.
          if (finalizeAbortRef.current === uvtpAbort) finalizeAbortRef.current = null;
          setTranscribing(false);
        }
      };

      // Deliver a streamed final via the SAME contract as the UVTP success path:
      // gen-guard (drop on session close — no ghost send) -> best-effort vocab
      // correction -> onTranscript -> voice:finalize_complete breadcrumb.
      const deliverStreamingFinal = (rawText) => {
        // Gen guard — a session close (cancelPendingTranscript/forceCleanup)
        // before the streamed final resolved bumps finalizeGenRef; drop the
        // transcript so it never lands in a closed session.
        if (myGen !== finalizeGenRef.current) {
          logVoiceEvent('stt_stream.final', 'skip', 'Session closed before streamed final delivered', {
            captured_gen: myGen,
            current_gen: finalizeGenRef.current,
            recording_id: recordingId,
            trace_id: traceIdRef.current,
          });
          return;
        }
        // TICKET-076 — vocab correction for the streamed final is applied in the
        // CONSUMER (useVoiceConversation.handleTranscript), which runs BOTH the
        // exact domain_vocabulary alias pass (normalizeTranscriptSync) AND the
        // conservative proper-noun phonetic pass against the user's LIVE entity
        // names (correctProperNounsSync). Centralizing there gives the streaming
        // and UVTP finals ONE correction surface and keeps this hot path free of
        // an async vocab fetch. Hand the raw final straight through.
        const correctedText = rawText || '';
        if (onTranscript) onTranscript(correctedText);
        emitVoiceEvent('voice:finalize_complete', {
          voiceRecordId: capturedVoiceRecordId,
          recordingId,
          contextType: contextType || null,
          contextId: contextId || null,
        });
      };

      (async () => {
        try {
          // T3 — streaming-first when the live socket engaged this turn.
          if (streamingActive && streamer) {
            streamer.commit(); // request the final (gpt-realtime-whisper has no server VAD)
            let outcome = { text: null, error: new Error('no streaming result') };
            try {
              outcome = await streamer.awaitFinal(STREAMING_FINAL_DEADLINE_MS);
            } catch (err) {
              outcome = { text: null, error: err instanceof Error ? err : new Error(String(err?.message ?? err)) };
            }
            // Streamed final arrived cleanly → deliver it; UVTP never runs.
            if (!outcome.error && typeof outcome.text === 'string') {
              deliverStreamingFinal(outcome.text);
              try { streamer.stop(); } catch { /* spine-emit-ok: stop is idempotent mic/socket release */ }
              // TICKET-CODEX-3368858430 — clear the MediaRecorder chunk buffer so
              // the next turn's UVTP fallback path doesn't concatenate blobs from
              // this already-transcribed turn and produce a stale/merged transcript.
              // runUvtpFallback does this itself; the streaming-success path must too.
              localChunkBlobsRef.current = [];
              setTranscribing(false);
              return;
            }
            const failureCode = outcome.error && outcome.error.message && /deadline/.test(outcome.error.message)
              ? 'final_timeout'
              : 'stream_error';
            // TICKET-VOICE-FINAL-DEADLOCK — salvage the turn with the
            // accumulated interim before resorting to UVTP. The interim is
            // exactly the live text the user already saw on screen; delivering
            // it keeps the turn alive (LLM + TTS proceed) instead of dying in
            // the fallback. Only an EMPTY interim (no partials at all) still
            // routes to the buffered-blob UVTP path.
            const interim = (typeof streamer.getInterim === 'function' ? streamer.getInterim() : '') || '';
            if (interim.trim()) {
              logVoiceEvent('stt_stream.final', 'ok', 'streamed final missing — using accumulated interim as transcript', {
                trace_id: traceIdRef.current,
                recording_id: recordingId,
                used_interim: true,
                text_length: interim.trim().length,
                error_code: failureCode,
              });
              deliverStreamingFinal(interim.trim());
              try { streamer.stop(); } catch { /* spine-emit-ok: stop is idempotent mic/socket release */ }
              // Clear the fallback buffer — this turn is served (mirrors the
              // streaming-success path, TICKET-CODEX-3368858430).
              localChunkBlobsRef.current = [];
              setTranscribing(false);
              return;
            }
            // No interim at all → fall back to the buffered-blob UVTP path.
            logVoiceEvent('stt_stream.fallback', 'error', 'streaming final not received -> UVTP', {
              trace_id: traceIdRef.current,
              recording_id: recordingId,
              error_code: failureCode,
            });
            try { streamer.stop(); } catch { /* spine-emit-ok: stop is idempotent mic/socket release */ }
            await runUvtpFallback();
            return;
          }
          // Streaming OFF / never engaged → unchanged batch UVTP path.
          await runUvtpFallback();
        } catch (err) {
          // Last-resort guard so a seam bug can never strand `transcribing`.
          logVoiceEvent('uvtp_finalize', 'error', err?.message || 'conversation finalize seam error', {
            recording_id: recordingId,
            trace_id: traceIdRef.current,
            error_code: 'finalize_seam',
          });
          setTranscribing(false);
        }
      })();
      cleanup();
      return;
    }

    // TICKET-VOICE-CONVO-UVTP — legacy finalize IIFE is dictation-only.
    // The conversation branch above returns before reaching this point;
    // the explicit `if (mode !== 'conversation')` guard below documents
    // the contract and short-circuits any future refactor that
    // accidentally reaches here from a conversation path. The
    // `voice.finalize_recording` POST that follows posts only when mode
    // is 'dictation'.
    if (mode !== 'conversation') (async () => {
      // Hoisted so the outer finally can null the ref iff this IIFE still owns it.
      let controller = null;
      try {
        const auth = await getAuthHeaders();
        const timer = startTimer('finalize_recording');
        controller = new AbortController();
        finalizeAbortRef.current = controller;
        const abortTimer = setTimeout(() => controller.abort(), FINALIZE_TIMEOUT_MS);
        let resp;
        try {
          resp = await fetch(VOICE_API, {
            method: 'POST',
            signal: controller.signal,
            headers: {
              'Content-Type': 'application/json',
              ...(traceIdRef.current ? { 'X-Trace-Id': traceIdRef.current } : {}),
              ...auth,
            },
            body: JSON.stringify({
              action: 'voice.finalize_recording',
              recording_id: recordingId,
              trace_id: traceIdRef.current,
              wait_for_transcript: true,
              stt_provider: resolveDefaultProvider(),
            }),
          });
        } finally {
          clearTimeout(abortTimer);
        }
        // Generation bumped → session was cancelled. Drop the response.
        if (myGen !== finalizeGenRef.current) {
          timer.ok(`finalize cancelled by session close (gen ${myGen} → ${finalizeGenRef.current})`);
          logVoiceEvent('finalize_cancelled', 'ok', 'Session closed before finalize returned', {
            captured_gen: myGen,
            current_gen: finalizeGenRef.current,
            recording_id: recordingId,
          });
          return;
        }
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data.error) {
          timer.fail(data.error || `HTTP ${resp.status}`);
          throw new Error(data.error || `Finalize failed (${resp.status})`);
        }
        timer.ok(`finalize ok: ${(data.text || '').length} chars`);
        if (onTranscript) onTranscript(data.text || '');
        emitVoiceEvent('voice:finalize_complete', {
          voiceRecordId: capturedVoiceRecordId,
          recordingId,
          contextType: contextType || null,
          contextId: contextId || null,
        });
      } catch (err) {
        // Silence the error path too when the abort came from our own cancel.
        if (myGen !== finalizeGenRef.current) {
          logVoiceEvent('finalize_cancelled', 'ok', 'Finalize aborted by session close', {
            captured_gen: myGen,
            current_gen: finalizeGenRef.current,
            recording_id: recordingId,
          });
          return;
        }
        const { stage, message: msg } = classifyFinalizeError(err);
        console.error('[useWhisperRecording] finalize error:', { stage, msg });
        // TICKET-024B — on a fetch REJECT (network drop / AbortError timeout)
        // the request throws before `timer.fail()` is reached, so this catch
        // previously emitted only a DOM event (emitVoiceEvent) — NO spine row.
        // That silent catch is why the iPhone "stuck transcribing" spinner was
        // undiagnosable. Emit a spine error here so the failure is visible in
        // /admin/observability. SPINE EMIT CONTRACT (hot-zone voice hook).
        logVoiceEvent('finalize_recording', 'error', msg, {
          trace_id: traceIdRef.current,
          recording_id: recordingId,
          stage,
          error_code: err?.name || stage,
          error_message: msg,
        });
        if (onError) onError(msg, { stage });
        emitVoiceEvent('voice:finalize_error', {
          voiceRecordId: capturedVoiceRecordId,
          recordingId,
          error: msg,
          stage,
        });
      } finally {
        if (controller && finalizeAbortRef.current === controller) finalizeAbortRef.current = null;
        setTranscribing(false);
      }
    })();

    cleanup();
  }, [cleanup, onTranscript, onError, contextType, contextId, mode]);

  const setMicEnabled = useCallback((enabled) => {
    // Sticky: persist intent so new streams acquired later (auto-VAD
    // short-lived streams, recording streams) inherit the mute state.
    micMutedRef.current = !enabled;
    const stream = streamRef.current;
    if (!stream || typeof stream.getAudioTracks !== 'function') return;
    stream.getAudioTracks().forEach((t) => { try { t.enabled = !!enabled; } catch { /* noop */ } });
  }, []);

  const muteMic = useCallback(() => setMicEnabled(false), [setMicEnabled]);
  const unmuteMic = useCallback(() => setMicEnabled(true), [setMicEnabled]);

  // 2026-04-20 Phase 1 (audit P0-3): cancel any in-flight finalize so the
  // detached IIFE inside stopRecording doesn't call onTranscript after the
  // session has been closed by endSession/unmount. Increments finalizeGenRef
  // (invalidates the captured gen inside the IIFE) AND aborts the fetch
  // controller so the request doesn't keep Whisper burning tokens server-side.
  const cancelPendingTranscript = useCallback(() => {
    finalizeGenRef.current += 1;
    if (finalizeAbortRef.current) {
      try { finalizeAbortRef.current.abort(); } catch { /* noop */ }
      finalizeAbortRef.current = null;
    }
    // TICKET-CODEX-3199616566: clear buffered conversation chunks on cancel so
    // stale blobs from a prior turn cannot leak into the next turn's concatenation.
    localChunkBlobsRef.current = [];
    setTranscribing(false);
  }, []);

  // 2026-04-20 (session 3) — bulletproof cleanup for endSession.
  //
  // stopRecording's normal path early-returns when recorder isn't 'recording'
  // (state already inactive from a prior VAD stop, rotate race, etc.). That
  // leaves chunkTimer still scheduled + streamRef alive → red mic dot stays
  // on + chunk_upload rows keep landing in app_error_logs. The CEO has been
  // seeing Chrome's mic indicator stuck "hot" for minutes after closing the
  // overlay.
  //
  // forceCleanup() unconditionally:
  //   - Bumps finalizeGenRef + aborts any in-flight finalize fetch
  //   - Clears both rolling timers
  //   - Stops the current mediaRecorder regardless of state
  //   - Releases streamRef via micRegistry (so the global registry is consistent)
  //   - Calls stopAllMics() as the "nuke option" — sweeps any stream this hook
  //     didn't register (defense in depth; no-op if registry is already clean)
  //   - Resets recording/transcribing/duration UI state so the overlay doesn't
  //     linger in "Processing..."
  //   - Emits a `mic_stop` observability event so SQL can confirm teardown
  //
  // Call from endSession / unmount / any path where "make sure the mic is off
  // regardless of what state we're in" is the correct semantic.
  const forceCleanup = useCallback((reason = 'force_cleanup') => {
    finalizeGenRef.current += 1;
    if (finalizeAbortRef.current) {
      try { finalizeAbortRef.current.abort(); } catch { /* noop */ }
      finalizeAbortRef.current = null;
    }
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch { /* noop */ }
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      try { releaseMic(streamRef.current); } catch { /* noop */ }
      streamRef.current = null;
    }
    // T3 — kill the streaming-STT socket + its own mic on force cleanup.
    if (streamingSttRef.current) {
      try { streamingSttRef.current.stop(); } catch { /* spine-emit-ok: stop is idempotent mic/socket release */ }
      streamingSttRef.current = null;
    }
    streamingActiveRef.current = false;
    try { stopAllMics(reason); } catch { /* noop */ }
    // TICKET-CODEX-3199616566: clear buffered conversation chunks on force
    // cleanup so stale blobs cannot leak into the next session's concatenation.
    localChunkBlobsRef.current = [];
    setRecording(false);
    setTranscribing(false);
    setDurationMs(0);
    logVoiceEvent('mic_stop', 'ok', `Force cleanup: ${reason}`, {
      reason,
      recording_id: recordingIdRef.current || null,
    });
  }, []);

  return {
    recording,
    transcribing,
    startRecording,
    stopRecording,
    durationMs,
    chunksUploaded,
    chunksPending,
    muteMic,
    unmuteMic,
    cancelPendingTranscript,
    forceCleanup,
  };
}
